import { XStack, YStack } from '@/components/ui/layout/layout';
import type { McpPermission } from '@/features/integrations/integrations.types';
import { cn } from '@/lib/utils';

import { MCP_PERMISSIONS } from './server-permissions';

function PermissionSettingsLink({ permission }: { permission: McpPermission }) {
  const { label, requestAccess } = MCP_PERMISSIONS[permission];
  return (
    <button
      type="button"
      onClick={() => void requestAccess()}
      className="font-medium text-foreground underline underline-offset-2 hover:text-foreground-subtle"
    >
      System Settings → Privacy &amp; Security → {label}
    </button>
  );
}

function PermissionSteps({ permission }: { permission: McpPermission }) {
  return (
    <ol className="list-decimal pl-4 text-xs leading-relaxed text-foreground-muted">
      <li>
        Open <PermissionSettingsLink permission={permission} />
      </li>
      <li>If macOS asks, quit and reopen Littlebird</li>
    </ol>
  );
}

/** Single-line grant hint under a server row. */
export function FullDiskAccessHint({ granted }: { granted: boolean }) {
  if (granted) {
    return (
      <p className="text-xs leading-relaxed text-foreground-muted">
        Run setup again from the menu to finish.
      </p>
    );
  }
  return (
    <p className="text-xs leading-relaxed text-foreground-muted">
      Open <PermissionSettingsLink permission="fullDiskAccess" /> to grant
      access. If macOS asks, quit and reopen Littlebird.
    </p>
  );
}

function MissingPermissionRow({ permission }: { permission: McpPermission }) {
  const { label, useGranted } = MCP_PERMISSIONS[permission];
  const granted = useGranted();

  return (
    <YStack className="min-w-0 gap-1.5">
      <XStack className="items-center gap-2">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span
          className={cn(
            'text-xs rounded-full px-2 py-0.5',
            granted
              ? 'bg-success-3 text-success-11'
              : 'bg-warning-3 text-warning-11'
          )}
        >
          {granted ? 'Granted' : 'Missing'}
        </span>
      </XStack>
      {granted ? (
        <p className="text-xs leading-relaxed text-foreground-muted">
          Testing again with the new permission…
        </p>
      ) : (
        <PermissionSteps permission={permission} />
      )}
    </YStack>
  );
}

/** Permission rows inside the failed-test card of the add-server dialog. */
export function MissingPermissionsDetails({
  missingPermissions,
}: {
  missingPermissions: readonly McpPermission[];
}) {
  return missingPermissions.map((permission) => (
    <MissingPermissionRow key={permission} permission={permission} />
  ));
}
